<script setup>
import {
  ref,
  computed,
  onBeforeUnmount,
  watch,
  nextTick,
  createApp,
  h,
} from 'vue';
import { useNuxtApp } from '#app';
import { MAP_STYLES, ICON_ACTIVE, ICON_DEFAULT } from '@/configs/map';
import { useRequest } from '@/composables/useRequest';
import MapRegionCard from '@/components/cards/MapRegionCard.vue';
import PointsSlider from '@/components/Maps/PointsSlider.vue';
import MapPointCard from '@/components/cards/MapPointCard.vue';
import MapPointAutocenterCard from '@/components/cards/MapPointAutocenterCard.vue';
import { useGlobalStore } from '@/stores/global';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isCarPage: {
    type: Boolean,
    default: false,
  },
});
const globalStore = useGlobalStore();
const googleMapsApiKey = computed(() => globalStore.googleMapsApiKey);
const isMapInitialized = ref(false);

const nuxtApp = useNuxtApp();
const loading = ref(false);

const searchQuery = ref('');
const filteredRegions = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return props.data.regions;
  return (props.data.regions || []).filter((region) =>
    region.title.toLowerCase().includes(query),
  );
});
const clearSearchQuery = () => {
  searchQuery.value = '';
};

const view = ref('all');
const selectedRegion = ref(null);

const TEMPLATE = computed(() => props.data.template || '');
const SERVICE = computed(() => props.data.service || '');
const CAR_ID = computed(() => props.data.car_id || '');
const REGIONS = computed(() =>
  (props.data.regions || []).map((region) => ({
    name: region.title,
    code: region.label,
  })),
);
const dynamicFilters = ref([]);
const BLOCK_FILTERS = computed(() => {
  if (
    TEMPLATE.value === 'autoservice_list' ||
    TEMPLATE.value === 'autoservice_list_one'
  ) {
    return dynamicFilters.value || [];
  }

  return props.data.filters || [];
});

const filtersState = ref({});
const filterDefaults = ref({});

function ensureAllOption(filter) {
  if (filter?.type !== 'select') return filter;
  const opts = Array.isArray(filter.options) ? filter.options : [];
  const hasAll = opts.some((o) => o?.value === 'all');
  if (hasAll) return filter;
  return {
    ...filter,
    options: [{ value: 'all', title: $t('networkMap.all') }, ...opts],
  };
}

function getDefaultValue(filter) {
  if (filter?.type === 'checkbox') return Boolean(filter.default ?? false);
  if (filter?.type === 'select') return filter.default ?? 'all';
  return filter?.default ?? null;
}

const isSyncingFilters = ref(false);

function initFiltersState() {
  isSyncingFilters.value = true;

  const nextState = { ...filtersState.value };
  const nextDefaults = {};

  for (const raw of BLOCK_FILTERS.value || []) {
    const f = ensureAllOption(raw);

    let def = getDefaultValue(f);

    if (f.id === 'services' && SERVICE.value) {
      def = SERVICE.value;
    }

    nextDefaults[f.id] = def;

    // preserve existing value if present
    if (!(f.id in nextState)) {
      nextState[f.id] = def;
    }
  }

  // remove deleted filters
  Object.keys(nextState).forEach((key) => {
    if (!(key in nextDefaults)) {
      delete nextState[key];
    }
  });

  filtersState.value = nextState;
  filterDefaults.value = nextDefaults;

  nextTick(() => {
    isSyncingFilters.value = false;
  });
}

watch(
  BLOCK_FILTERS,
  () => {
    initFiltersState();
  },
  { immediate: true },
);

function resetFilterToDefault(id) {
  if (!(id in filterDefaults.value)) return;
  filtersState.value[id] = filterDefaults.value[id];
}

function resetSelectsToDefault() {
  for (const raw of BLOCK_FILTERS.value || []) {
    const f = ensureAllOption(raw);
    if (f.type === 'select') resetFilterToDefault(f.id);
  }
}
function isFilterDisabledByOthers(filterId) {
  for (const otherFilter of BLOCK_FILTERS.value) {
    if (otherFilter.disables && otherFilter.disables[filterId]) {
      const currentValue = filtersState.value[otherFilter.id];
      const disabledValues = otherFilter.disables[filterId];
      if (disabledValues.includes(currentValue)) {
        return true;
      }
    }
  }
  return false;
}

const UI_FILTERS = computed(() => {
  return (BLOCK_FILTERS.value || []).map((raw) => {
    const f = ensureAllOption(raw);
    let disabled = loading.value;

    if (isFilterDisabledByOthers(f.id)) {
      disabled = true;
    }

    if (f.id === 'warranty') {
      disabled = disabled || !filtersState.value['service_center'];
    }

    return {
      ...f,
      label: f.label ?? f.title ?? '',
      disabled,
    };
  });
});
watch(
  () => ({ ...filtersState.value }),
  () => {
    for (const raw of BLOCK_FILTERS.value || []) {
      const f = ensureAllOption(raw);

      if (isFilterDisabledByOthers(f.id)) {
        if (filtersState.value[f.id] !== filterDefaults.value[f.id]) {
          resetFilterToDefault(f.id);
        }
      }

      if (f.id === 'warranty' && !filtersState.value['service_center']) {
        resetFilterToDefault(f.id);
      }
    }
  },
  { deep: true },
);

// Reset selects on region change for autoservice_* templates (per requirement)
watch(
  () => selectedRegion.value,
  (region, prev) => {
    if (!region || region === prev) return;
    if (
      TEMPLATE.value === 'autoservice_list' ||
      TEMPLATE.value === 'autoservice_list_one'
    ) {
      resetSelectsToDefault();
    }
  },
);

const centers = ref([]);
function buildAutocenterQuery() {
  const q = {};
  if (filtersState.value?.dealer) q.dealer = 1;
  if (filtersState.value?.distributor) q.distributor = 1;
  if (filtersState.value?.service_center) q.service_center = 1;
  if (filtersState.value?.warranty) q.warranty = 1;
  if (filtersState.value?.services) q.services = filtersState.value?.services;
  if (filtersState.value?.warranty_brand)
    q.warranty_brand = filtersState.value?.warranty_brand;
  if ((TEMPLATE.value === 'service' || TEMPLATE.value === 'autoservice_list_one') && SERVICE.value.length > 0)
    q.services = SERVICE.value;
  return q;
}

async function loadCentersByRegion(regionCode) {
  if (!regionCode) return;

  loading.value = true;

  try {
    const query = buildAutocenterQuery();
    const opts = Object.keys(query).length ? { query } : {};
    let templatePath = TEMPLATE.value;

    if (
      TEMPLATE.value === 'autoservice_list' ||
      TEMPLATE.value === 'autoservice_list_one'
    ) {
      templatePath = 'autoservice_list';
    }

    const url =
      TEMPLATE.value === 'autocenter_list_car'
        ? `map/${templatePath}/${regionCode}?car_id=${CAR_ID.value}`
        : `map/${templatePath}/${regionCode}`;

    const data = await useRequest(url, opts);

    centers.value = data?.items || data || [];
    if (
      TEMPLATE.value === 'autoservice_list' ||
      TEMPLATE.value === 'autoservice_list_one'
    ) {
      dynamicFilters.value = data?.filters || [];
    }
  } catch (err) {
    console.error('[Map Error]', err);
    centers.value = [];
  } finally {
    setTimeout(() => {
      loading.value = false;
    }, 300);
  }
}

const chooseRegion = (id) => {
  view.value = 'region';
  selectedRegion.value = id;
};

const mapEl = ref(null);
let map = null;
let infoWindow = null;
let markers = [];
let activeMarkerIndex = -1;
let infoApp = null;
let infoContainer = null;

function unmountInfoCard() {
  if (infoApp) {
    try {
      infoApp.unmount();
    } catch {}
    infoApp = null;
  }
  infoContainer = null;
}

let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (gmapsPromise) return gmapsPromise;
  gmapsPromise = new Promise((resolve, reject) => {
    const id = 'gmaps-script';
    if (document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.id = id;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gmapsPromise;
}
function initMap() {
  if (!mapEl.value || !window.google?.maps) return;

  map = new window.google.maps.Map(mapEl.value, {
    center: { lat: 48.3794, lng: 31.1656 },
    zoom: 6,
    styles: MAP_STYLES,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
  });

  infoWindow = new window.google.maps.InfoWindow();
  window.google.maps.event.addListener(infoWindow, 'closeclick', () => {
    unmountInfoCard();
    resetActiveMarker();
  });
  renderMarkers();
}

function getPointCardProps(item) {
  const data = {};
  if (
    TEMPLATE.value === 'autocenter_list' ||
    TEMPLATE.value === 'autocenter_list_car'
  ) {
    data.value = {
      id: item?.id,
      type: 'autocenter',
      salon: item,
      service: item?.service,
      name: item?.name || '',
      marker: item?.marker,
      brandsText: $t('networkMap.brands'),
      chooseAutocenterText: $t('networkMap.choose_autocenter'),
      leaveRequestText: $t('networkMap.leave_request'),
      serviceCenterText: $t('networkMap.service_center'),
      autocenterText: $t('networkMap.autocenter'),
      isServiceActive: filtersState.value['service_center'],
      services: item?.services || [],
      servicesText: $t('networkMap.services'),
    };
  } else if (
    TEMPLATE.value === 'autoservice_list' ||
    TEMPLATE.value === 'autoservice_list_one'
  ) {
    data.value = {
      id: item?.id,
      type: 'autocenter',
      name: item?.name || '',
      marker: item?.marker,
      address: item?.address || '',
      phone: item?.phone || '',
      time: item?.time || '',
      services: item?.services || [],
      link: item?.link || '',
      servicesText: $t('networkMap.services'),
      chooseAutocenterText: $t('networkMap.choose_autocenter'),
      leaveRequestText: $t('networkMap.leave_request'),
    };
  } else if (TEMPLATE.value === 'autocenter_list_buses') {
    data.value = {
      id: item?.id,
      type: 'autocenter',
      name: item?.name || '',
      marker: item.marker,
      address: item?.address || '',
      phone: item?.phone || '',
      time: item?.time || '',
      link: item?.link || '',
      chooseAutocenterText: $t('networkMap.choose_autocenter'),
      leaveRequestText: $t('networkMap.leave_request'),
    };
  } else if (TEMPLATE.value === 'service') {
    data.value = {
      id: item?.id,
      type: 'service',
      name: item?.name || '',
      marker: item.marker,
      address: item?.address || '',
      phone: item?.phone || '',
      time: item?.time || '',
      link: item?.link || '',
      leaveRequestText: $t('networkMap.leave_request'),
      service: SERVICE.value || '',
    };
  } else {
    data.value = {
      id: item?.id,
      type: TEMPLATE.value || '',
      name: item?.name || '',
      marker: item.marker,
      address: item?.address || '',
      phone: item?.phone || '',
      time: item?.time || '',
      brands: item?.brands || [],
      link: item?.link || '',
      fuel: item?.fuel || [],
      services: item?.services || [],
      fuelText: $t('networkMap.fuel'),
      servicesText: $t('networkMap.available_services'),
    };
  }
  return data.value;
}

function clearMarkers() {
  markers.forEach((m) => m.setMap(null));
  markers = [];
  activeMarkerIndex = -1;
}

function resetActiveMarker() {
  if (activeMarkerIndex !== -1 && markers[activeMarkerIndex]) {
    markers[activeMarkerIndex].setIcon(ICON_DEFAULT);
    activeMarkerIndex = -1;
  }
}

function activateMarker(index) {
  if (!markers.length || index < 0 || index >= markers.length) return;
  if (activeMarkerIndex !== -1 && markers[activeMarkerIndex]) {
    markers[activeMarkerIndex].setIcon(ICON_DEFAULT);
  }
  activeMarkerIndex = index;
  const marker = markers[index];
  marker.setIcon(ICON_ACTIVE);
  unmountInfoCard();
  infoContainer = document.createElement('div');
  const item = centers.value[index];
  const cardProps = getPointCardProps(item);
  const componentToMount =
    TEMPLATE.value === 'autocenter_list' ||
    TEMPLATE.value === 'autocenter_list_car'
      ? MapPointAutocenterCard
      : MapPointCard;

  infoApp = createApp({
    render() {
      return h(componentToMount, cardProps);
    },
  });
  Object.assign(infoApp._context.provides, nuxtApp.vueApp._context.provides);

  infoApp.config.globalProperties.$primevue =
    nuxtApp.vueApp.config.globalProperties.$primevue;

  infoApp.mount(infoContainer);

  infoWindow.setContent(infoContainer);
  infoWindow.open({ anchor: marker, map });
}

function renderMarkers() {
  if (!map) return;
  clearMarkers();
  const bounds = new window.google.maps.LatLngBounds();
  let hasPoints = false;

  centers.value.forEach((item, idx) => {
    const lat = Number(item?.marker?.lat);
    const lng = Number(item?.marker?.lng);

    if (!isNaN(lat) && !isNaN(lng)) {
      const position = { lat, lng };
      const marker = new window.google.maps.Marker({
        position,
        map,
        icon: ICON_DEFAULT,
        title: item.name || '',
      });

      marker.addListener('click', () => activateMarker(idx));
      markers.push(marker);

      bounds.extend(position);
      hasPoints = true;
    }
  });

  if (hasPoints) {
    map.fitBounds(bounds, 48);

    const listener = window.google.maps.event.addListener(map, 'idle', () => {
      if (map.getZoom() > 15) map.setZoom(15);
      window.google.maps.event.removeListener(listener);
    });
  } else {
    map.setCenter({ lat: 48.3794, lng: 31.1656 });
    map.setZoom(6);
  }
}
watch(
  () => [view.value, googleMapsApiKey.value],
  async ([currentView, apiKey]) => {
    if (currentView !== 'region' || !apiKey) return;

    if (!selectedRegion.value && REGIONS.value?.length) {
      selectedRegion.value = REGIONS.value[0].code;
    }
    if (selectedRegion.value) {
      await loadCentersByRegion(selectedRegion.value);
    }

    await nextTick();

    try {
      await loadGoogleMaps(apiKey);
      initMap();
      renderMarkers();

      isMapInitialized.value = true;
    } catch (error) {
      console.error('Failed to load Google Maps with dynamic API key:', error);
    }
  },
  { immediate: true },
);

watch(
  () => centers.value,
  () => {
    if (map) renderMarkers();
  },
  { deep: true },
);
watch(
  () => [selectedRegion.value, JSON.stringify(filtersState.value)],
  async ([region]) => {
    if (isSyncingFilters.value) return;

    if (!region || view.value !== 'region') return;

    await loadCentersByRegion(region);

    await nextTick();

    renderMarkers();
  },
);
onBeforeUnmount(() => {
  if (infoWindow) infoWindow = null;
  clearMarkers();
  map = null;
});
</script>

<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      isCarPage
        ? 'bg-gray'
        : data?.background_color
          ? `bg-${data.background_color}`
          : '',
      isCarPage
        ? 'section--top-xxl'
        : data?.spacing?.top
          ? `section--top-${data.spacing.top}`
          : '',
      isCarPage
        ? 'section--bottom-xxl'
        : data?.spacing?.bottom
          ? `section--bottom-${data.spacing.bottom}`
          : '',
      view === 'all' ? 'overflow' : '',
      data.template === 'autoservice_list' ||
      data.template === 'autoservice_list_one'
        ? 'network--autoservice'
        : '',
    ]"
  >
    <div class="container">
      <div
        :class="[
          'network',
          `network--${view}`,
          isCarPage
            ? `network--gray`
            : data?.background_color
              ? `network--${data.background_color}`
              : '',
        ]"
      >
        <div
          v-if="loading"
          :class="[
            'network__overlay',
            isCarPage
              ? `network__overlay--gray`
              : `network__overlay--${data?.background_color}`,
          ]"
          class=""
          aria-hidden="true"
        >
          <div class="network__overlay-spinner"></div>
        </div>
        <template v-if="view === 'all'">
          <template v-if="isCarPage">
            <h2
              class="mb-0 network__title h2"
              data-aos="fade-in"
              data-aos-delay="100"
              v-html="$t('networkMap.car_map_title')"
            ></h2>
          </template>
          <template v-else>
            <h2
              v-if="data.title"
              class="mb-0 network__title h2"
              data-aos="fade-in"
              data-aos-delay="100"
              v-html="data.title"
            ></h2>
          </template>

          <div class="network__regions">
            <div
              class="network__regions-top"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <template v-if="isCarPage">
                <p
                  class="mb-0 network__subtitle body-2-regular"
                  v-html="$t('networkMap.car_map_subtitle')"
                ></p>
              </template>
              <template v-else>
                <p
                  v-if="data.subtitle"
                  class="mb-0 network__subtitle body-2-regular"
                  v-html="data.subtitle"
                ></p>
              </template>

              <div class="network__search">
                <InputGroup
                  class="network__search-field"
                  :class="[
                    searchQuery ? 'network__search-field--with-value' : '',
                  ]"
                >
                  <InputGroupAddon class="network__search-icon">
                    <CIcon
                      name="search-black"
                      width="16"
                      height="16"
                      class="network__search-icon-el"
                    />
                  </InputGroupAddon>

                  <InputText
                    v-model="searchQuery"
                    type="text"
                    size="small"
                    :placeholder="$t('networkMap.field_placeholder')"
                    enter-key-hint="search"
                    inputmode="search"
                    class="network__search-input search-input"
                  />

                  <InputGroupAddon
                    v-if="searchQuery"
                    class="network__search-button"
                    @click="clearSearchQuery"
                  >
                    <CIcon
                      class="network__search-button-icon"
                      name="close-black"
                      width="16"
                      height="16"
                    />
                  </InputGroupAddon>
                </InputGroup>

                <p
                  v-if="filteredRegions?.length === 0"
                  class="mb-0 network__search-error body-3-medium"
                >
                  {{ $t('networkMap.regions_empty') }}
                </p>
              </div>
            </div>

            <ul
              v-if="filteredRegions?.length"
              class="m-0 p-0 network__regions-list"
            >
              <template
                v-for="(item, itemIndex) in filteredRegions"
                :key="item.label"
              >
                <li
                  v-if="item.quantity > 0"
                  class="m-0 network__region"
                  data-aos="fade-up"
                  :data-aos-delay="itemIndex >= 1 ? 100 + 50 * itemIndex : 100"
                >
                  <MapRegionCard
                    :title="item.title"
                    :label="item.label"
                    :quantity="item.quantity"
                    :brands="item.brands"
                    :services="item.services"
                    @click="chooseRegion(item.label)"
                  />
                </li>
              </template>
            </ul>
          </div>
        </template>
        <template v-else>
          <template v-if="isCarPage">
            <h2
              class="mb-0 network__title h2"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
            >
              {{ $t('networkMap.car_map_points_title') }}
            </h2>
          </template>
          <template v-else>
            <h2
              v-if="data.points_title"
              class="mb-0 network__title h2"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
            >
              {{ data.points_title }}
            </h2>
          </template>

          <div class="network__info">
            <div
              v-if="
                (data.filters && data.filters.length > 0) ||
                filteredRegions?.length > 0
              "
              :class="[
                'network__form',
                data.filters &&
                data.filters.length > 0 &&
                filteredRegions?.length === 0
                  ? 'network__form--only-filters'
                  : data.filters &&
                      data.filters.length === 0 &&
                      filteredRegions?.length > 0
                    ? 'network__form--only-regions'
                    : '',
              ]"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
            >
              <FloatLabel
                class="network__floatlabel floatlabel-onwhite"
                variant="in"
              >
                <Select
                  v-model="selectedRegion"
                  filter
                  :filter-placeholder="$t('networkMap.specify_region')"
                  input-id="selectedRegion"
                  :options="REGIONS"
                  option-label="name"
                  option-value="code"
                  variant="filled"
                  class="network__select select"
                  size="large"
                  overlay-class=""
                >
                  <template #dropdownicon>
                    <CIcon
                      name="direction-black"
                      width="16"
                      height="16"
                      class="p-select-dropdown__icon"
                    />
                  </template>
                  <template #filtericon>
                    <CIcon name="search-black" width="16" height="16" />
                  </template>
                </Select>

                <label for="selectedRegion" class="find-car__form-label">
                  {{ $t('networkMap.region') }}
                </label>
              </FloatLabel>
              <div v-if="UI_FILTERS.length" class="network__filters">
                <template v-for="f in UI_FILTERS" :key="f.id">
                  <label
                    v-if="f.type === 'checkbox'"
                    class="network__checkbox checkbox"
                  >
                    <Checkbox
                      v-model="filtersState[f.id]"
                      :binary="true"
                      :disabled="f.disabled"
                      class="checkbox-input"
                    >
                      <template #icon>
                        <CIcon
                          name="check-white"
                          width="12"
                          height="12"
                          class="checkbox-icon"
                        />
                      </template>
                    </Checkbox>
                    <span
                      class="checkbox-label body-3-regular"
                      v-html="f.label"
                    ></span>
                  </label>
                  <div
                    v-else-if="f.type === 'select'"
                    class="network__select-filter"
                  >
                    <FloatLabel
                      class="network__floatlabel floatlabel-onwhite"
                      variant="in"
                    >
                      <Select
                        v-model="filtersState[f.id]"
                        :options="f.options || []"
                        option-label="title"
                        option-value="value"
                        variant="filled"
                        class="network__select select"
                        size="large"
                        :disabled="f.disabled"
                        :input-id="`filter-${f.id}`"
                      >
                        <template #dropdownicon>
                          <CIcon
                            name="direction-black"
                            width="16"
                            height="16"
                            class="p-select-dropdown__icon"
                          />
                        </template>
                      </Select>

                      <label
                        :for="`filter-${f.id}`"
                        class="find-car__form-label"
                      >
                        {{ f.label }}
                      </label>
                    </FloatLabel>
                  </div>
                </template>
              </div>
            </div>
            <div class="network__slider">
              <PointsSlider
                :data="centers"
                :type="data.template"
                :service="data.service"
                :is-service-active="filtersState['service_center']"
              />
            </div>
          </div>
          <div
            ref="mapEl"
            class="network__map"
            :aria-label="data.title"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          ></div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
$list-size: 30;
.network {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 48px;
    &--region {
      gap: 64px;
    }
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 48);
    &--region {
      @include responsive-spacing(gap, 64);
    }
  }
  &--autoservice {
    #{$parent} {
      &__filters {
        padding: 0;
        @include media-breakpoint-up(sm) {
          grid-template-columns: 1fr;
        }
        @include media-breakpoint-up(md) {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      &__select-filter {
        border-top: 1px solid $divider;
        @include media-breakpoint-up(md) {
          border-top: unset;
          border-left: 1px solid $divider;
        }
        &:first-child {
          border: unset;
          @include media-breakpoint-up(sm) {
            border-top: 1px solid $divider;
          }
          @include media-breakpoint-up(md) {
            border: unset;
          }
        }
      }
    }
  }
  &--white {
    #{$parent} {
    }
  }
  &--black {
    #{$parent} {
      &__form {
        background-color: rgba($surface-container-opacity, 0.5);
        border: 1px solid $on-surface-variant-2;
      }
      &__filters {
        border-top: 1px solid $on-surface-variant-2;
        @include media-breakpoint-up(md) {
          border-left: 1px solid $on-surface-variant-2;
        }
      }
      &__map {
        background: $lime-green;
      }
      &__select-filter {
        border-top: 1px solid $on-surface-variant-2;
        &:first-child {
          border: unset;
        }
      }
    }
  }
  &--gray {
    #{$parent} {
      &__form {
        background-color: $white;
        border: 1px solid $divider;
      }
      &__filters {
        border-top: 1px solid $divider;
        @include media-breakpoint-up(sm) {
          border-top: unset;
        }
        @include media-breakpoint-up(md) {
          border-left: 1px solid $divider;
        }
      }
      &__map {
        background: $lime-green;
      }
    }
  }
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
  }

  &__overlay-spinner {
    animation: catalog-spin 0.8s linear infinite;
    border: 3px solid $divider;
    border-radius: 50%;
    border-top-color: $black;
    height: 40px;
    margin: 0 auto;
    position: sticky;
    top: calc(50vh - 20px);
    width: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 3);
      @include responsive-spacing(height, 40);
      @include responsive-spacing(width, 40);
      top: calc(50vh - px-to-vw(20));
    }
  }
  &__title {
    margin: 0;
  }
  &__subtitle {
    max-width: 80%;
    @include media-breakpoint-up(sm) {
      max-width: unset;
    }
  }
  &__search {
    position: relative;
    $parent: &;
    &-field {
      align-items: center;
      display: grid;
      grid-template-columns: auto 16px;
      gap: 12px;
      border-bottom: 1px solid $black;
      grid-template-areas: 'search-input search-icon';
      &--with-value {
        grid-template-areas: 'search-input search-clear';
        #{$parent} {
          &-icon {
            display: none;
          }
        }
      }

      @include media-breakpoint-up(sm) {
        grid-template-columns: 16px auto 16px;
        grid-template-areas: 'search-icon search-input search-input';
        &--with-value {
          grid-template-areas: 'search-icon search-input search-clear';
          #{$parent} {
            &-icon {
              display: block;
            }
          }
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(gap, 12);
        grid-template-columns: px-to-vw(16) auto px-to-vw(16);
      }
    }
    &-icon {
      border: unset;
      grid-area: search-icon;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-el {
        height: 100%;
        width: 100%;
      }
    }
    &-input {
      border-end-end-radius: 0;
      border-start-end-radius: 0;
      border-radius: 0;
      grid-area: search-input;
      width: 100%;
    }
    &-button {
      grid-area: search-clear;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-error {
      background-color: $white;
      border-radius: 4px;
      box-shadow: 0px 4px 36px 0px #00000026;
      left: 0;
      position: absolute;
      padding: 16px;
      width: 100%;
      z-index: 1;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
        @include responsive-spacing(border-radius, 4);
      }
    }
  }
  &__regions {
    display: flex;
    flex-direction: column;
    gap: 38px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
    }
    &-top {
      display: grid;
      gap: 24px;
      @include media-breakpoint-up(sm) {
        align-items: center;
        grid-template-columns: repeat(2, 284px);
        justify-content: space-between;
      }
      @include media-breakpoint-up(xl) {
        grid-template-columns: px-to-vw(284) px-to-vw(284);
        @include responsive-spacing(gap, 24);
      }
    }
    &-list {
      display: grid;
      gap: 8px;
      list-style: none;
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(2, 1fr);
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(3, 1fr);
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
  }
  &__region {
    @for $i from 1 through $list-size {
      &:nth-child(#{$i}) {
        z-index: $list-size - $i + 1;
      }
    }
  }
  &__form {
    border-radius: 4px;
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    @include media-breakpoint-up(sm) {
      &--only-regions {
        width: 50%;
      }
      &--only-filters {
        width: 100%;
      }
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 4fr 8fr;
      &--only-regions {
        grid-template-columns: 1fr;
        width: 33.33%;
      }
      &--only-filters {
        grid-template-columns: repeat(2, 1fr);
        width: 66.66%;
      }
    }
  }
  &__floatlabel {
  }
  &__filters {
    display: grid;
    gap: 4px;
    grid-template-columns: 1fr;
    padding: 16px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
      padding: 24px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      column-gap: 40px;
      grid-template-columns: repeat(2, 1fr);
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-left-width, 1);
      @include responsive-spacing(column-gap, 40);
      @include responsive-spacing(padding, 24);
    }
  }
  &__checkbox {
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__map {
    width: 100%;
    height: 620px;
    border-radius: 8px;
    overflow: hidden;
    @include media-breakpoint-up(sm) {
      height: 840px;
    }
    @include media-breakpoint-up(md) {
      height: 600px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 600);
      @include responsive-spacing(border-radius, 8);
    }
    &:deep(.gm-style-iw) {
      padding: 0;
      max-height: 493px !important;
      max-width: 312px !important;
      @include media-breakpoint-up(sm) {
        max-height: 479px !important;
        max-width: 348px !important;
      }
      @include media-breakpoint-up(md) {
        max-width: 396px !important;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(max-width, 396, true);
        @include responsive-spacing(max-height, 479, true);
      }
    }
    &:deep(.gm-style-iw-chr) {
      position: absolute;
      right: 16px;
      top: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(right, 16);
        @include responsive-spacing(top, 16);
      }
    }
    &:deep(.gm-style-iw-chr button) {
      height: 32px !important;
      opacity: 1;
      width: 32px !important;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 32, true);
        @include responsive-spacing(width, 32, true);
      }
    }
    &:deep(.gm-style-iw-chr button span) {
      background: url('/assets/icons/close-black.svg') no-repeat -1px -1px;
      background-size: contain;
      background-color: transparent !important;
      height: 16px !important;
      margin: auto !important;
      mask-image: unset !important;
      width: 16px !important;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 16, true);
        @include responsive-spacing(width, 16, true);
      }
    }
    &:deep(.gm-style-iw-ch) {
      display: none;
    }
  }
  &__slider {
    width: 100%;
  }
}
</style>
